import type { ActorRole, MeResponse, Session } from '@dpem/contracts';
import { create } from 'zustand';

/**
 * The session, in memory only.
 *
 * There is deliberately no `persist` middleware and no `localStorage` anywhere
 * near this file. The access token lives in a JS closure for the lifetime of the
 * tab; the *refresh* token is an HttpOnly, same-origin cookie the client cannot
 * read. That split is the whole reason the contract has no refresh token in any
 * schema: a token in web storage is readable by any script that gets injected,
 * and it survives the tab, so an XSS becomes a persistent account takeover
 * rather than a page reload.
 *
 * The cost of that choice is a flash of "restoring session" on every hard
 * reload, because the token has to be re-minted from the cookie. That cost is
 * paid in `AuthProvider`, once, at boot.
 *
 * This module holds no React. It is a store, so the api client can read the
 * current token from a plain function without a hook and without a re-render.
 */

/** `Session` is the login/refresh response shape: token, lifetime, user. */
export type SessionUser = Session['user'];

export type SessionStatus =
  /** Boot: a refresh is in flight and we do not yet know if there is a session. */
  | 'restoring'
  /** No session. Not an error — this is what a first visit looks like. */
  | 'anonymous'
  | 'authenticated';

export interface SessionState {
  readonly status: SessionStatus;
  readonly accessToken: string | null;
  readonly user: SessionUser | null;
  /** Effective roles as the server reports them, not as the token claims them. */
  readonly roles: readonly ActorRole[];
  /** Epoch ms at which `accessToken` stops being accepted. */
  readonly expiresAt: number | null;
  /**
   * Set when a session ended because the server rejected it rather than because
   * the user asked. Login reads it to explain why they are back at the door.
   */
  readonly expiredReason: 'session-expired' | null;
}

export interface SessionActions {
  /** A fresh token from login or refresh. `now` is injected so tests own time. */
  readonly grant: (session: Session, now: number) => void;
  /** Server said no. Clears the token and marks why. */
  readonly expire: () => void;
  /** The user asked to leave, or boot found no cookie. */
  readonly clear: () => void;
  /** `authMe` is the authority on roles; it may disagree with an older token. */
  readonly syncIdentity: (me: MeResponse) => void;
}

const ANONYMOUS: SessionState = {
  status: 'anonymous',
  accessToken: null,
  user: null,
  roles: [],
  expiresAt: null,
  expiredReason: null,
};

export const useSessionStore = create<SessionState & SessionActions>((set) => ({
  ...ANONYMOUS,
  // Boot starts as `restoring`: `anonymous` would flash the login screen at a
  // user who is in fact signed in, and that flash is indistinguishable from
  // being logged out.
  status: 'restoring',

  grant: (session, now) =>
    set({
      status: 'authenticated',
      accessToken: session.accessToken,
      user: session.user,
      roles: session.user.roles,
      expiresAt: now + session.expiresIn * 1000,
      expiredReason: null,
    }),

  expire: () => set({ ...ANONYMOUS, expiredReason: 'session-expired' }),

  clear: () => set({ ...ANONYMOUS }),

  syncIdentity: (me) => set({ user: me.user, roles: me.roles }),
}));

/**
 * The token, for callers that are not components — the api client's
 * `getAccessToken`, chiefly. Reading through the store rather than closing over
 * a value means a refresh mid-flight is picked up by the next request.
 */
export const currentAccessToken = (): string | null => useSessionStore.getState().accessToken;

/** Milliseconds until the access token expires; 0 once it has. */
export const millisecondsUntilExpiry = (state: SessionState, now: number): number =>
  state.expiresAt === null ? 0 : Math.max(0, state.expiresAt - now);
